import React from 'react';
import {
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  StarIcon,
  ArchiveBoxIcon,
  TagIcon,
  ClockIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { RichNote } from '../../types/richNotes';

interface RichNoteViewerProps {
  note: RichNote;
  onClose: () => void;
  onEdit: (note: RichNote) => void;
  onDelete: (noteId: string) => void;
  onToggleFavorite: (noteId: string) => void;
  onArchive: (noteId: string) => void;
}

const RichNoteViewer: React.FC<RichNoteViewerProps> = ({
  note,
  onClose,
  onEdit,
  onDelete,
  onToggleFavorite,
  onArchive,
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    pink: 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    gray: 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800',
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`flex items-start justify-between p-4 sm:p-6 md:p-8 border-b-2 ${colorClasses[note.color]} rounded-t-2xl sm:rounded-t-3xl`}>
          <div className="flex-1 min-w-0 pr-2 sm:pr-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2 sm:mb-3 md:mb-4">
              {note.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <ClockIcon className="w-4 h-4" />
                Módosítva: {formatDate(note.updatedAt)}
              </span>
              {note.category && (
                <span className="flex items-center gap-1.5">
                  <FolderIcon className="w-4 h-4" />
                  {note.category}
                </span>
              )}
              {note.isPinned && (
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  📌 Rögzítve
                </span>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => onToggleFavorite(note.id)}
              className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={note.isFavorite ? 'Kedvencekből eltávolítás' : 'Kedvencekhez adás'}
            >
              {note.isFavorite ? (
                <StarIconSolid className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              ) : (
                <StarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
              )}
            </button>
            <button
              onClick={() => onEdit(note)}
              className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Szerkesztés"
            >
              <PencilSquareIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
            </button>
            <button
              onClick={() => {
                onArchive(note.id);
                onClose();
              }}
              className="hidden sm:block p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Archiválás"
            >
              <ArchiveBoxIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
            <button
              onClick={() => {
                onDelete(note.id);
                onClose();
              }}
              className="hidden sm:block p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
              title="Törlés"
            >
              <TrashIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors sm:ml-2"
              title="Bezárás"
            >
              <XMarkIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <div
            className="prose prose-sm sm:prose-base md:prose-lg dark:prose-invert max-w-none
              [&>h1]:text-3xl [&>h1]:font-bold [&>h1]:mb-4 [&>h1]:mt-6
              [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:mb-3 [&>h2]:mt-5
              [&>h3]:text-xl [&>h3]:font-bold [&>h3]:mb-2 [&>h3]:mt-4
              [&>p]:mb-3 [&>p]:leading-relaxed [&>p]:text-gray-700 [&>p]:dark:text-gray-300
              [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-3
              [&>ol]:list-decimal [&>ol]:ml-6 [&>ol]:mb-3
              [&>li]:mb-1 [&>li]:text-gray-700 [&>li]:dark:text-gray-300
              [&>pre]:bg-gray-100 [&>pre]:dark:bg-gray-900 [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:mb-3 [&>pre]:overflow-x-auto
              [&>a]:text-blue-600 [&>a]:dark:text-blue-400 [&>a]:underline [&>a]:hover:text-blue-700
              [&>blockquote]:border-l-4 [&>blockquote]:border-gray-300 [&>blockquote]:dark:border-gray-600 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:mb-3
              [&>strong]:font-bold [&>strong]:text-gray-900 [&>strong]:dark:text-gray-100
              [&>em]:italic"
            dangerouslySetInnerHTML={{ __html: note.content }}
          />
        </div>

        {/* Footer with tags */}
        {note.tags && note.tags.length > 0 && (
          <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <TagIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
              {note.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-1 sm:px-3 sm:py-1.5 md:px-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Metadata footer */}
        <div className="px-4 py-3 sm:px-6 sm:py-3 md:px-8 md:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl sm:rounded-b-3xl">
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Létrehozva: {formatDate(note.createdAt)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RichNoteViewer;
